import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import {
  createCourseTemplateAsAdmin,
  getCourseTemplateByIdAsAdmin,
  getCourseTemplatesAsAdmin,
  loadMoreCourseTemplatesAsAdmin,
} from './adminApi';
import { RootState } from '../..';
import { ISectionTemplateForAdmin } from '../SectionTemplateSlice';

export interface ICourseTemplateForAdmin {
  _id: string;
  name: string;
  title: string;
  course: string;
  classDay?: string;
  classTime?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
  amount?: string;
  sections?: ISectionTemplateForAdmin[];
}

export interface IInitialCourseTemplatesState {
  status: 'idle' | 'loading' | 'rejected';
  adminCourseTemplatesData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourseTemplateForAdmin>;
  };
  adminCourseTemplate?: ICourseTemplateForAdmin;
}

const initialState: IInitialCourseTemplatesState = {
  status: 'idle',
  // Admin
  adminCourseTemplatesData: undefined,
  adminCourseTemplate: undefined,
};

export const CourseTemplatesState = createSlice({
  name: 'courseTemplates',
  initialState,
  reducers: {
    resetAdminCourseTemplatesData: (state) => {
      state.adminCourseTemplatesData = undefined;
    },
    resetAdminCourseTemplate: (state) => {
      state.adminCourseTemplate = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCourseTemplateAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getCourseTemplatesAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newAdminCourseTemplatesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCourseTemplatesData = newAdminCourseTemplatesData;
      })
      .addCase(loadMoreCourseTemplatesAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (state.adminCourseTemplatesData) {
          const newAdminCourseTemplatesData = {
            ...state.adminCourseTemplatesData,
            list: [
              ...(state.adminCourseTemplatesData.list || []),
              ...(action.payload.docs || []),
            ],
          };

          state.adminCourseTemplatesData = newAdminCourseTemplatesData;
        }
      })
      .addCase(getCourseTemplateByIdAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        state.adminCourseTemplate = result;
      });

    builder.addMatcher(
      isPending(
        createCourseTemplateAsAdmin,
        getCourseTemplatesAsAdmin,
        loadMoreCourseTemplatesAsAdmin,
        getCourseTemplateByIdAsAdmin
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        createCourseTemplateAsAdmin,
        getCourseTemplatesAsAdmin,
        loadMoreCourseTemplatesAsAdmin,
        getCourseTemplateByIdAsAdmin
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const {
  resetAdminCourseTemplatesData,
  resetAdminCourseTemplate,
} = CourseTemplatesState.actions;

export const getCourseTemplatesState = (
  state: RootState
): RootState['courseTemplates'] => state.courseTemplates;

export * from './adminApi';
export default CourseTemplatesState.reducer;
